import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Download, Route as RouteIcon, Users, X, Zap } from 'lucide-react';

export interface RotaConfig {
    data: string;
    prioridade: 'baixa' | 'media' | 'alta';
    observacoes: string;
}

interface RotaConfigModalProps {
    isOpen: boolean;
    qtdLeads: number;
    kwhTotal: number;
    /** Rótulo dos responsáveis-destino ("você" | "Fulano" | "N vendedores") */
    destinosLabel: string;
    /** Quantidade de responsáveis-destino (define se cria 1 rota ou uma por responsável) */
    destinosCount: number;
    onClose: () => void;
    /** Importa os leads e agenda a visita de cada um na data escolhida (distribuído entre os responsáveis) */
    onConfirmRota: (config: RotaConfig) => Promise<void>;
    /** Apenas importa os leads, sem agendar visita (distribuído entre os responsáveis) */
    onImportarSemRota: () => Promise<void>;
}

const amanha = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Configuração da rota operacional antes da importação dos leads (Prospecção Geo). */
const RotaConfigModal: React.FC<RotaConfigModalProps> = ({
    isOpen, qtdLeads, kwhTotal, destinosLabel, destinosCount, onClose, onConfirmRota, onImportarSemRota,
}) => {
    const [data, setData] = useState(amanha());
    const [prioridade, setPrioridade] = useState<'baixa' | 'media' | 'alta'>('media');
    const [observacoes, setObservacoes] = useState('');
    const [salvando, setSalvando] = useState(false);
    const varios = destinosCount > 1;

    useEffect(() => {
        if (isOpen) {
            setData(amanha());
            setPrioridade('media');
            setObservacoes('');
            setSalvando(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const confirmar = async () => {
        if (!data || salvando) return;
        setSalvando(true);
        try {
            await onConfirmRota({ data, prioridade, observacoes: observacoes.trim() });
        } finally {
            setSalvando(false);
        }
    };

    const soImportar = async () => {
        if (salvando) return;
        setSalvando(true);
        try {
            await onImportarSemRota();
        } finally {
            setSalvando(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                            <RouteIcon size={18} />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Agendar Visitas dos Leads</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                {qtdLeads} lead{qtdLeads !== 1 ? 's' : ''} selecionado{qtdLeads !== 1 ? 's' : ''} · <Zap size={10} className="inline -mt-0.5 text-amber-500" /> {Math.round(kwhTotal).toLocaleString('pt-BR')} kWh estimados
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Data da Visita <span className="text-red-500">*</span></label>
                            <input
                                type="date"
                                value={data}
                                onChange={e => setData(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Prioridade</label>
                            <select
                                value={prioridade}
                                onChange={e => setPrioridade(e.target.value as 'baixa' | 'media' | 'alta')}
                                className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                            >
                                <option value="baixa">🟢 Baixa</option>
                                <option value="media">🟡 Média</option>
                                <option value="alta">🔴 Alta</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Responsável pela execução</label>
                        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                            <Users size={15} className="text-gray-400 shrink-0" />
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 capitalize">{destinosLabel}</span>
                            <span className="text-[11px] text-gray-400">· definido no filtro "Importar para"</span>
                        </div>
                        {varios && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                                Os leads serão divididos entre os {destinosCount} responsáveis — <b>uma rota por pessoa</b>.
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Observações da Rota <span className="text-gray-400 font-normal">(opcional)</span></label>
                        <textarea
                            value={observacoes}
                            onChange={e => setObservacoes(e.target.value)}
                            rows={2}
                            placeholder='Ex.: "Priorizar empresas com consumo acima de 10.000 kWh."'
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                        />
                    </div>

                    <p className="text-[11px] text-gray-400 leading-relaxed">
                        Ao confirmar: os leads são importados para o CRM e as visitas entram no calendário (Rotas → Agenda) na data escolhida{varios ? <>, distribuídas entre os {destinosCount} responsáveis</> : null}.
                        No dia, gere a rota otimizada com um clique em <b>Criar Rota Inteligente</b>.
                    </p>

                    <div className="space-y-2">
                        <button
                            onClick={confirmar}
                            disabled={!data || salvando}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                        >
                            <CalendarDays size={15} /> {salvando ? 'Agendando...' : `Importar e Agendar Visitas (${qtdLeads})`}
                        </button>
                        <button
                            onClick={soImportar}
                            disabled={salvando}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-40"
                        >
                            <Download size={13} /> Só importar os leads (sem visita)
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RotaConfigModal;
